if (!customElements.get('mj-authentic-reviews')) {
  customElements.define('mj-authentic-reviews', class extends HTMLElement {
    connectedCallback() {
      this.stopPhotoLoop?.();
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-dialog-content]');
      if (!this.dialog || !this.content) return;
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-open]');
        if (trigger) {
          const template = [...this.querySelectorAll('template[data-detail]')].find(item => item.dataset.detail === trigger.dataset.open);
          if (template) {
            this.content.replaceChildren(template.content.cloneNode(true));
            this.dialog.showModal();
            this.syncPhotoPlayback?.();
          }
        }
        if (event.target.closest('[data-close]')) this.dialog.close();
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.content.replaceChildren();
        this.syncPhotoPlayback?.();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const block = [...this.querySelectorAll('[data-shopify-editor-block]')].find(item => {
          try { return JSON.parse(item.dataset.shopifyEditorBlock).id === event.detail.blockId; }
          catch { return false; }
        });
        block?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'instant' });
      }, options);
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const syncPhotos = () => {
        this.stopPhotoLoop?.();
        if (this.mobile.matches && !this.reducedMotion.matches) this.startPhotoLoop();
      };
      this.mobile.addEventListener('change', syncPhotos, options);
      this.reducedMotion.addEventListener('change', syncPhotos, options);
      syncPhotos();
    }
    startPhotoLoop() {
      const rail = this.querySelector('.mj-voices__photos');
      const track = rail?.querySelector('.mj-voices__photo-track');
      const originals = [...(track?.children || [])];
      if (originals.length < 2 || typeof track.animate !== 'function') return;
      const controller = new AbortController();
      const on = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: controller.signal });
      const clones = originals.map(photo => {
        const clone = photo.cloneNode(true);
        clone.dataset.photoClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('button').forEach(button => button.tabIndex = -1);
        clone.querySelectorAll('template').forEach(template => template.remove());
        track.append(clone);
        return clone;
      });
      rail.scrollLeft = 0;
      rail.dataset.photoLoop = 'true';
      const speed = 18;
      let animation, width = 0, duration = 0, resumeTimer, drag;
      let visible = false, hovered = false, focused = false, holdUntil = 0, suppressClickUntil = 0;
      const normalizeTime = time => ((time % duration) + duration) % duration;
      const sync = () => {
        if (!animation) return;
        const playing = visible && !document.hidden && !hovered && !focused && !drag && !this.dialog.open && performance.now() >= holdUntil;
        if (playing && animation.playState !== 'running') animation.play();
        else if (!playing && animation.playState !== 'paused') animation.pause();
      };
      this.syncPhotoPlayback = sync;
      const hold = () => {
        clearTimeout(resumeTimer);
        holdUntil = performance.now() + 3500;
        resumeTimer = setTimeout(sync, 3510);
        sync();
      };
      const measure = () => {
        rail.style.setProperty('--voices-photo-size', `${Math.min(240, Math.max(156, rail.clientWidth * .48))}px`);
        const nextWidth = clones[0].getBoundingClientRect().left - originals[0].getBoundingClientRect().left;
        if (nextWidth <= 0 || (animation && Math.abs(nextWidth - width) < .01)) return;
        const phase = duration ? normalizeTime(Number(animation?.currentTime || 0)) / duration : 0;
        animation?.cancel();
        width = nextWidth;
        duration = width / speed * 1000;
        animation = track.animate([
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(${-width}px, 0, 0)` }
        ], { duration, iterations: Infinity, easing: 'linear' });
        animation.id = 'mj-voices-photos';
        animation.pause();
        animation.currentTime = phase * duration;
        sync();
      };
      const resize = new ResizeObserver(measure);
      resize.observe(rail);
      measure();
      const intersection = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; sync(); });
      intersection.observe(rail);
      on(document, 'visibilitychange', sync);
      on(rail, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; sync(); } });
      on(rail, 'pointerleave', () => { hovered = false; sync(); });
      on(rail, 'focusin', event => {
        focused = event.target.matches(':focus-visible');
        if (focused && animation && duration) {
          const index = originals.indexOf(event.target.closest('.mj-voices__photo'));
          if (index >= 0) animation.currentTime = index * duration / originals.length;
          rail.scrollLeft = 0;
        }
        sync();
      });
      on(rail, 'focusout', event => { focused = !!event.relatedTarget && rail.contains(event.relatedTarget) && event.relatedTarget.matches(':focus-visible'); sync(); });
      on(rail, 'pointerdown', event => {
        if (event.button !== 0 || !animation) return;
        suppressClickUntil = 0;
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, time: Number(animation.currentTime || 0), moved: false };
        sync();
      });
      const release = event => {
        if (!drag || event.pointerId !== drag.id) return;
        const { id, moved } = drag;
        drag = null;
        if (moved) suppressClickUntil = performance.now() + 600;
        if (rail.hasPointerCapture(id)) rail.releasePointerCapture(id);
        delete rail.dataset.dragging;
        hold();
      };
      on(rail, 'pointermove', event => {
        if (!drag || event.pointerId !== drag.id || !duration) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { release(event); return; }
        if (!drag.moved && Math.abs(dx) <= 8) return;
        drag.moved = true;
        rail.dataset.dragging = 'true';
        rail.setPointerCapture(event.pointerId);
        animation.currentTime = normalizeTime(drag.time - dx / speed * 1000);
        event.preventDefault();
      }, { passive: false });
      on(window, 'pointerup', release);
      on(window, 'pointercancel', release);
      on(rail, 'lostpointercapture', release);
      on(rail, 'click', event => {
        if (performance.now() < suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          suppressClickUntil = 0;
        }
      }, { capture: true });
      on(rail, 'wheel', event => {
        if (!animation || !duration || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        animation.currentTime = normalizeTime(Number(animation.currentTime || 0) + event.deltaX / speed * 1000);
        event.preventDefault();
        hold();
      }, { passive: false });
      on(rail, 'keydown', event => {
        if (!animation || !duration || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        focused = true;
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const index = originals.indexOf(event.target.closest('.mj-voices__photo'));
        if (index >= 0) {
          const next = (index + direction + originals.length) % originals.length;
          animation.currentTime = next * duration / originals.length;
          originals[next].querySelector('button').focus({ preventScroll: true });
        } else animation.currentTime = normalizeTime(Number(animation.currentTime || 0) + direction * duration / originals.length);
        rail.scrollLeft = 0;
        sync();
      });
      this.stopPhotoLoop = () => {
        clearTimeout(resumeTimer);
        animation?.cancel();
        controller.abort();
        resize.disconnect();
        intersection.disconnect();
        clones.forEach(clone => clone.remove());
        delete rail.dataset.photoLoop;
        delete rail.dataset.dragging;
        rail.style.removeProperty('--voices-photo-size');
        rail.scrollLeft = 0;
        this.syncPhotoPlayback = null;
        this.stopPhotoLoop = null;
      };
    }
    disconnectedCallback() {
      this.stopPhotoLoop?.();
      this.dialog?.close();
      this.controller?.abort();
    }
  });
}
